/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC, MouseEvent, useMemo } from 'react';
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuItemProps,
  MenuItemPropsVariantOverrides,
  Typography,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { ScrollYPositionKeys } from '../../../util/consts';
import { saveScrollYPosition } from '../../../util/helpers';
import MaterialSymbol from '../../MaterialSymbol';

import './NavigationMenuItem.scss';

export interface INavigationMenuItemProps extends MenuItemProps {
  label: string;
  prefixMuiIcon?: string | JSX.Element;
  scrollYPosition?: ScrollYPositionKeys;
  variant?: keyof MenuItemPropsVariantOverrides;
}

const NavigationMenuItem: FC<INavigationMenuItemProps> = ({
  label,
  prefixMuiIcon,
  scrollYPosition,
  onClick,
  className,
  children,
  ...menuItemProps
}: INavigationMenuItemProps): JSX.Element => {
  const onMenuItemClick = (event: MouseEvent<HTMLLIElement, globalThis.MouseEvent>): void => {
    if (scrollYPosition) {
      saveScrollYPosition({
        sessionStorageKey: scrollYPosition,
        position: 0,
      });
    }

    onClick && onClick(event);
  };

  const prefixIcon = useMemo(
    (): JSX.Element | undefined =>
      typeof prefixMuiIcon === 'string' ? (
        <MaterialSymbol>{prefixMuiIcon}</MaterialSymbol>
      ) : (
        prefixMuiIcon
      ),
    [prefixMuiIcon],
  );

  return (
    <MenuItem
      {...menuItemProps}
      onClick={onMenuItemClick}
      className={`fl-navigation-menu-item${className ? ` ${className}` : ''}`}
    >
      {prefixIcon && (
        <ListItemIcon className='fl-navigation-menu-item__prefix-icon'>{prefixIcon}</ListItemIcon>
      )}

      <ListItemText>
        <Typography noWrap>{label}</Typography>
      </ListItemText>

      <ArrowForwardIosIcon className='fl-navigation-menu-item__postfix-icon' />
      {children}
    </MenuItem>
  );
};

export default NavigationMenuItem;
